import { z } from "zod";
/**
 * Global platform support configuration. Not organization-scoped.
 * Suspension copy is an i18n key in application code, not a field here.
 */
export const SUPPORT_EMAIL_MAX_LENGTH = 254;
export const SUPPORT_URL_MAX_LENGTH = 2048;
export const SUPPORT_CONTACT_MAX_LENGTH = 32;
export const SUPPORT_CONTACT_MIN_DIGITS = 7;
export const SUPPORT_CONTACT_MAX_DIGITS = 15;
const EMPTY_SETTINGS = {
    supportEmail: null,
    supportPhone: null,
    supportWhatsApp: null,
    supportUrl: null,
};
export const systemSettingsResponseSchema = z.object({
    supportEmail: z.string().nullable(),
    supportPhone: z.string().nullable(),
    supportWhatsApp: z.string().nullable(),
    supportUrl: z.string().nullable(),
});
export const EMPTY_SYSTEM_SETTINGS = { ...EMPTY_SETTINGS };
/**
 * Phone-like support contact. Trims elsewhere. Rejects blank values and
 * strings that are not a plausible phone (letters, too few or too many digits).
 * Does not normalize to E.164.
 */
export function isPlausibleSupportContact(value) {
    if (value.length === 0 || value.length > SUPPORT_CONTACT_MAX_LENGTH) {
        return false;
    }
    if (!/^[0-9+().\-\s]+$/.test(value)) {
        return false;
    }
    const plusCount = value.split("+").length - 1;
    if (plusCount > 1 || (plusCount === 1 && !value.startsWith("+"))) {
        return false;
    }
    const digitCount = value.replace(/\D/g, "").length;
    return digitCount >= SUPPORT_CONTACT_MIN_DIGITS && digitCount <= SUPPORT_CONTACT_MAX_DIGITS;
}
export function isHttpOrHttpsUrl(value) {
    if (value.length === 0 || value.length > SUPPORT_URL_MAX_LENGTH || /\s/.test(value)) {
        return false;
    }
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    }
    catch {
        return false;
    }
}
const supportEmailSchema = z
    .string()
    .trim()
    .min(1)
    .max(SUPPORT_EMAIL_MAX_LENGTH)
    .email();
const supportUrlSchema = z
    .string()
    .trim()
    .min(1)
    .max(SUPPORT_URL_MAX_LENGTH)
    .refine(isHttpOrHttpsUrl, { message: "Must be an http or https URL" });
const supportContactSchema = z
    .string()
    .trim()
    .min(1)
    .max(SUPPORT_CONTACT_MAX_LENGTH)
    .refine(isPlausibleSupportContact, { message: "Must be a phone-like value" });
/**
 * Partial update. Omitted keys are unchanged. Explicit null clears a field.
 * Unknown keys are rejected.
 */
export const updateSystemSettingsRequestSchema = z
    .object({
    supportEmail: supportEmailSchema.nullable().optional(),
    supportPhone: supportContactSchema.nullable().optional(),
    supportWhatsApp: supportContactSchema.nullable().optional(),
    supportUrl: supportUrlSchema.nullable().optional(),
})
    .strict();
//# sourceMappingURL=system-settings-contracts.js.map